import React from 'react';
import { pick } from 'js/utils/ramda.util';
import { Helmet } from 'react-helmet-async';
import { useDeviceClassNames } from 'device';
import { useLocation } from 'react-router-dom';
import { getURLParam } from 'js/utils/url.util';
import { useAppSelector, useUserSelector } from 'js/store/hooks';

import * as hocs from './hocs';
import * as hooks from './hooks';
import * as helpers from './helpers';
import { Layout } from './components/Layout';
import { PageLoader } from './components/PageLoader';

export const PageBare = ({ page, isLoading, fetchPageInfo }) => {
    const location = useLocation();
    const { lang, role } = useUserSelector(pick(['lang', 'role']));
    const { allowedLocales, defaultLocale } = useAppSelector(pick(['allowedLocales', 'defaultLocale']));
    const isPageAsPopUpDisabled = getURLParam(location.search, 'noPageAsPopup') === '1' || role === 'admin';

    const deviceClassNames = useDeviceClassNames();

    hooks.useSetColorScheme({ page });
    hooks.useLegacyPageRenderedTrigger({ page });
    hooks.useCMSIntegration({ page, fetchPageInfo });

    if (isLoading) {
        return <PageLoader /> ;
    }

    const pageStyles = helpers.getPageStyles({ page });
    const alternateLinks = helpers.getAlternateLinks({ page, allowedLocales, defaultLocale });
    const canonicalLinkUrl = helpers.getCanonicalLinkUrl({ page, defaultLocale, lang });
    const { title, robots, keywords, description, rootClassNames } = page;
    const helmet = (
        <Helmet htmlAttributes={{ class: helpers.getPageClassNames(rootClassNames, deviceClassNames) }}>
            <title>{title}</title>
            <meta name="robots" content={robots} />
            <meta name="keywords" content={keywords} />
            <meta name="description" content={description} />
            <meta name="referrer" content="no-referrer-when-downgrade" />
            {alternateLinks.map(alternateLink => (
                <link
                    rel={'alternate'}
                    href={alternateLink.href}
                    key={alternateLink.hrefLang}
                    hrefLang={alternateLink.hrefLang}
                />
            ))}
            <link rel="canonical" href={canonicalLinkUrl} />
            <style type="text/css">{pageStyles}</style>
        </Helmet>
    );

    return <>
        {helmet}
        {(!page.showAsPopUp || isPageAsPopUpDisabled) && <Layout page={page} />}
    </>;
};

export const Page = hocs.withCommonPage(PageBare);
